import { createContext, useReducer, Dispatch, ReactNode } from "react";
import type { PaymentMethodEntityTypesEnum, PaymentMethodVM } from "@propertelligent/client-api";

export interface PlaidAccountDetails {
  accountName?: string | null;
  accountNumberMask?: string | null;
  accountId?: string | null;
  itemId?: string | null;
  institutionName?: string | null;
  publicAccessToken?: string | null;
}

/** Entity config for saving Plaid payment method - pass when you want auto-save to backend */
export interface PlaidEntityConfig {
  entityTypeId: PaymentMethodEntityTypesEnum;
  entityId: number;
}

interface QuickstartState {
  linkSuccess: boolean;
  isItemAccess: boolean;
  isPaymentInitiation: boolean;
  linkToken: string | null;
  accessToken: string | null;
  itemId: string | null;
  isError: boolean;
  backend: boolean;
  products: string[];
  linkTokenError: {
    error_message: string;
    error_code: string;
    error_type: string;
  };
  plaidAccountDetails: PlaidAccountDetails | null;
  /** Config for saving Plaid account - when set, Link will call postApiPaymentMethod on success */
  plaidEntityConfig: PlaidEntityConfig | null;
  /** Custom label for the Plaid connect button (e.g. "Continue", "Connect Now") */
  plaidButtonLabel: string | null;
  /** When set, Link will call putApiPaymentMethod to update/reconnect this Plaid payment method */
  plaidReconnectPaymentMethod: PaymentMethodVM | null;
}

const initialState: QuickstartState = {
  linkSuccess: false,
  isItemAccess: true,
  isPaymentInitiation: false,
  linkToken: "", // Don't set to null or error message will show up briefly when site loads
  accessToken: null,
  itemId: null,
  isError: false,
  backend: true,
  products: ["transactions"],
  linkTokenError: {
    error_type: "",
    error_code: "",
    error_message: "",
  },
  plaidAccountDetails: null,
  plaidEntityConfig: null,
  plaidButtonLabel: null,
  plaidReconnectPaymentMethod: null,
};

type QuickstartAction = {
  type: "SET_STATE";
  state: Partial<QuickstartState>;
};

interface QuickstartContext extends QuickstartState {
  dispatch: Dispatch<QuickstartAction>;
}

const Context = createContext<QuickstartContext>(
  initialState as QuickstartContext
);

const { Provider } = Context;

export interface QuickstartProviderProps {
  children: ReactNode;
  /** Pass entity config to auto-save Plaid payment method on success. Use for Organization, Tenant, etc. */
  plaidEntityConfig?: PlaidEntityConfig | null;
  /** Custom label for the Plaid connect button (e.g. "Continue", "Connect Now") */
  plaidButtonLabel?: string | null;
  /** When set, Link will PUT to update/reconnect this Plaid payment method instead of POST */
  plaidReconnectPaymentMethod?: PaymentMethodVM | null;
}

export const QuickstartProvider: React.FC<QuickstartProviderProps> = ({
  children,
  plaidEntityConfig = null,
  plaidButtonLabel = null,
  plaidReconnectPaymentMethod = null,
}) => {
  const reducer = (
    state: QuickstartState,
    action: QuickstartAction
  ): QuickstartState => {
    switch (action.type) {
      case "SET_STATE":
        return { ...state, ...action.state };
      default:
        return { ...state };
    }
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <Provider
      value={{
        ...state,
        plaidEntityConfig: plaidEntityConfig ?? state.plaidEntityConfig,
        plaidButtonLabel: plaidButtonLabel ?? state.plaidButtonLabel ?? "Connect Now",
        plaidReconnectPaymentMethod: plaidReconnectPaymentMethod ?? state.plaidReconnectPaymentMethod,
        dispatch,
      }}
    >
      {children}
    </Provider>
  );
};

export default Context;
